import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { SectionHeading } from './shared/SectionHeading';
const G1 = 'https://cdn.magicpatterns.com/patterns/generated-images/17fba966-bcb8-4605-bc88-988034caf11d.jpg';
const G2 = 'https://cdn.magicpatterns.com/patterns/generated-images/ab3f01a9-fffc-4f42-8e73-68263eccdf6e.jpg';
const G3 = 'https://cdn.magicpatterns.com/patterns/generated-images/bbc5cc94-35a7-4067-b168-1502ce17c04b.jpg';
const HERO = 'https://cdn.magicpatterns.com/patterns/generated-images/26867317-615b-4aee-bbe1-f8601b45d196.jpg';
type Category = 'sve' | 'njega' | 'dom' | 'podrska';
interface GalleryItem {
  src: string;
  alt: string;
  category: Exclude<Category, 'sve'>;
  span: string;
}
const items: GalleryItem[] = [{
  src: HERO,
  alt: 'Njegovateljica sa starijom osobom u domu',
  category: 'njega',
  span: 'sm:row-span-2'
}, {
  src: G2,
  alt: 'Priprema zdravog obroka u kuhinji',
  category: 'dom',
  span: ''
}, {
  src: G3,
  alt: 'Ruke — njegovateljica drži ruku korisnika',
  category: 'podrska',
  span: ''
}, {
  src: G1,
  alt: 'Pomoć pri kretanju kroz dom',
  category: 'njega',
  span: 'sm:row-span-2'
}, {
  src: G3,
  alt: 'Topla podrška i pažnja',
  category: 'podrska',
  span: ''
}, {
  src: G2,
  alt: 'Svakodnevna pomoć u domaćinstvu',
  category: 'dom',
  span: ''
}];
const filters: {
  key: Category;
  label: string;
}[] = [{
  key: 'sve',
  label: 'Sve'
}, {
  key: 'njega',
  label: 'Njega'
}, {
  key: 'dom',
  label: 'Dom'
}, {
  key: 'podrska',
  label: 'Podrška'
}];
export function Gallery() {
  const [active, setActive] = useState<Category>('sve');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const filtered = useMemo(() => active === 'sve' ? items : items.filter(i => i.category === active), [active]);
  const openAt = (globalIndex: number) => setLightbox(globalIndex);
  const close = () => setLightbox(null);
  const go = (dir: 1 | -1) => setLightbox(cur => cur === null ? cur : (cur + dir + filtered.length) % filtered.length);
  return <section id="galerija" className="relative w-full bg-cream py-20 lg:py-28" data-id="element-138">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-139">
        <SectionHeading eyebrow="Galerija" title="Trenuci brige i topline" subtitle="Pogled u svakodnevicu naše njege — dostojanstvo, pažnja i istinska povezanost." data-id="element-140" />

        <div className="mt-10 flex flex-wrap justify-center gap-3" data-id="element-141">
          {filters.map(f => <button key={f.key} type="button" onClick={() => setActive(f.key)} className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all ${active === f.key ? 'bg-gold-gradient text-white shadow-gold' : 'border border-gold/30 bg-white text-ink hover:border-gold hover:bg-white'}`} data-id="element-142">
              {f.label}
            </button>)}
        </div>

        <motion.div layout className="mt-12 grid auto-rows-[220px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-143">
          <AnimatePresence mode="popLayout" data-id="element-144">
            {filtered.map((item, i) => <motion.button type="button" key={`${item.src}-${item.alt}-${i}`} layout initial={{
            opacity: 0,
            scale: 0.92
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.92
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => openAt(i)} className={`group relative overflow-hidden rounded-3xl shadow-glass ring-1 ring-black/5 ${item.span}`} aria-label={`Uvećaj sliku: ${item.alt}`} data-id="element-145">
                <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-146" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" data-id="element-147" />
                <div className="absolute inset-x-0 bottom-0 flex translate-y-3 items-center justify-between p-5 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100" data-id="element-148">
                  <span className="text-left text-sm font-medium text-white" data-id="element-149">
                    {item.alt}
                  </span>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/90 text-ink" data-id="element-150">
                    <ZoomInIcon className="h-4 w-4" data-id="element-151" />
                  </span>
                </div>
              </motion.button>)}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-152">
        {lightbox !== null && filtered[lightbox] && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm" onClick={close} role="dialog" aria-modal="true" aria-label="Pregled slike" data-id="element-153">
            <button type="button" onClick={close} aria-label="Zatvori" className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20" data-id="element-154">
              <XIcon className="h-5 w-5" data-id="element-155" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          go(-1);
        }} aria-label="Prethodna slika" className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-8" data-id="element-156">
              <ChevronLeftIcon className="h-6 w-6" data-id="element-157" />
            </button>
            <motion.img key={filtered[lightbox].src + lightbox} initial={{
          opacity: 0,
          scale: 0.94
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} src={filtered[lightbox].src} alt={filtered[lightbox].alt} onClick={e => e.stopPropagation()} className="max-h-[82vh] max-w-[90vw] rounded-2xl object-contain shadow-luxury" data-id="element-158" />
            <button type="button" onClick={e => {
          e.stopPropagation();
          go(1);
        }} aria-label="Sljedeća slika" className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-8" data-id="element-159">
              <ChevronRightIcon className="h-6 w-6" data-id="element-160" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}